import React, {CSSProperties, useEffect, useState} from 'react'
import axios from 'axios'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import toaster from '../../../../Utils/toaster'
import {useFeatureAccess} from '../../../hooks/useFeatureAccess'

const API_URL = process.env.REACT_APP_API_URL

type SettingsResponse = {
  isPersisted: boolean
  isEnabled: boolean
  defaultMinMarginPercent: number
  totalWindowMinutes: number
  bucketPoolPercent: number
  bucketMaxInterpreters: number | null
  bucketIntervalMinutes: number
  preAssignmentConfirmationWaitMinutes: number
  maxConfirmationRotations: number | null
  preAssignmentConfirmationMinLeadMinutes: number
  maxNoShowsInLast3Months: number | null
  minLeadTimeMinutes: number
  maxBucketsInWindow: number
}

const toNumber = (value: string): number => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toOptionalInt = (value: string): number | null => {
  if (value.trim() === '') return null
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

type ValidationRule = {invalid: boolean; message: string}

type SettingsFormValues = {
  defaultMinMarginPercent: number
  totalWindowMinutes: number
  bucketPoolPercent: number
  bucketMaxInterpreters: number | null
  bucketIntervalMinutes: number
  preAssignmentConfirmationWaitMinutes: number
  maxConfirmationRotations: number | null
  preAssignmentConfirmationMinLeadMinutes: number
  maxNoShowsInLast3Months: number | null
  minLeadTimeMinutes: number
}

const isOutOfRange = (value: number, min: number, max: number) => value < min || value > max

const isNotPositive = (value: number) => value <= 0

const isNegative = (value: number) => value < 0

const isOptionalBelow = (value: number | null, min: number) => value !== null && value < min

const buildValidationRules = (values: SettingsFormValues): ValidationRule[] => [
  {
    invalid: isOutOfRange(values.defaultMinMarginPercent, 0, 100),
    message: 'Default minimum profit margin must be between 0 and 100 percent',
  },
  {
    invalid: isNotPositive(values.totalWindowMinutes),
    message: 'Total outreach window must be greater than zero minutes',
  },
  {
    invalid: isNotPositive(values.bucketPoolPercent) || values.bucketPoolPercent > 100,
    message: 'Bucket size must be greater than 0 and no more than 100 percent of the pool',
  },
  {
    invalid: isNotPositive(values.bucketIntervalMinutes),
    message: 'Time between buckets must be greater than zero minutes',
  },
  {
    invalid: values.bucketIntervalMinutes > values.totalWindowMinutes,
    message: `Time between buckets cannot exceed the total outreach window of ${values.totalWindowMinutes} minutes`,
  },
  {
    invalid: isOptionalBelow(values.bucketMaxInterpreters, 1),
    message: 'Maximum interpreters per bucket must be blank, or 1 or more',
  },
  {
    invalid: isNegative(values.minLeadTimeMinutes),
    message: 'Minimum notice before the agent runs must be zero or more minutes',
  },
  {
    invalid: isNegative(values.preAssignmentConfirmationWaitMinutes),
    message: 'Time to confirm must be zero or more minutes',
  },
  {
    invalid: isOptionalBelow(values.maxConfirmationRotations, 1),
    message: 'Maximum interpreters to ask must be blank, or 1 or more',
  },
  {
    invalid: isNegative(values.preAssignmentConfirmationMinLeadMinutes),
    message: 'Stop asking before start must be zero or more minutes',
  },
  {
    invalid: isOptionalBelow(values.maxNoShowsInLast3Months, 0),
    message: 'Maximum no-shows in the last 3 months must be blank, or zero or more',
  },
]

const infoButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 15,
  height: 15,
  borderRadius: '50%',
  border: '2px solid #a3a242',
  backgroundColor: '#ffffff',
  color: '#a3a242',
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1,
  padding: 0,
  cursor: 'pointer',
  flexShrink: 0,
}

const SettingInfoTooltip = ({id, text}: {id: string; text: string}) => (
  <OverlayTrigger
    placement='top'
    trigger={['hover', 'focus']}
    overlay={
      <Tooltip id={id}>
        <div className='text-start'>{text}</div>
      </Tooltip>
    }
  >
    <button type='button' aria-label='More information' style={infoButtonStyle}>
      i
    </button>
  </OverlayTrigger>
)

export function AiAssignmentAgentSettings() {
  const access = useFeatureAccess('emails')
  const canEdit = access === 'edit'

  const [isLoading, setIsLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [isPersisted, setIsPersisted] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const [defaultMinMarginPercent, setDefaultMinMarginPercent] = useState(50)
  const [totalWindowMinutes, setTotalWindowMinutes] = useState(120)
  const [bucketPoolPercent, setBucketPoolPercent] = useState(25)
  const [bucketMaxInterpreters, setBucketMaxInterpreters] = useState<number | null>(null)
  const [bucketIntervalMinutes, setBucketIntervalMinutes] = useState(30)
  const [preAssignmentConfirmationWaitMinutes, setPreAssignmentConfirmationWaitMinutes] =
    useState(30)
  const [maxConfirmationRotations, setMaxConfirmationRotations] = useState<number | null>(null)
  const [preAssignmentConfirmationMinLeadMinutes, setPreAssignmentConfirmationMinLeadMinutes] =
    useState(0)
  const [maxNoShowsInLast3Months, setMaxNoShowsInLast3Months] = useState<number | null>(null)
  const [minLeadTimeMinutes, setMinLeadTimeMinutes] = useState(0)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const response = await axios.get<{result: SettingsResponse}>(
        `${API_URL}/ai-assignment-settings`
      )
      const data = response.data?.result

      if (!data) {
        setLoadFailed(true)
        toaster('error', 'Failed to load AI Assignment Agent settings')
        return
      }

      setIsPersisted(data.isPersisted)
      setIsEnabled(data.isEnabled)
      setDefaultMinMarginPercent(data.defaultMinMarginPercent)
      setTotalWindowMinutes(data.totalWindowMinutes)
      setBucketPoolPercent(data.bucketPoolPercent)
      setBucketMaxInterpreters(data.bucketMaxInterpreters)
      setBucketIntervalMinutes(data.bucketIntervalMinutes)
      setPreAssignmentConfirmationWaitMinutes(data.preAssignmentConfirmationWaitMinutes)
      setMaxConfirmationRotations(data.maxConfirmationRotations)
      setPreAssignmentConfirmationMinLeadMinutes(data.preAssignmentConfirmationMinLeadMinutes)
      setMaxNoShowsInLast3Months(data.maxNoShowsInLast3Months)
      setMinLeadTimeMinutes(data.minLeadTimeMinutes)
    } catch (error) {
      console.error('Error fetching AI Assignment Agent settings:', error)
      setLoadFailed(true)
      toaster('error', 'Failed to load AI Assignment Agent settings')
    } finally {
      setIsLoading(false)
    }
  }

  const maxBuckets =
    bucketIntervalMinutes > 0 && totalWindowMinutes > 0
      ? Math.max(1, Math.ceil(totalWindowMinutes / bucketIntervalMinutes))
      : 1

  const bucketsNeededFor = (poolSize: number) => {
    const size = bucketMaxInterpreters
      ? Math.min(Math.ceil((poolSize * bucketPoolPercent) / 100), bucketMaxInterpreters)
      : Math.ceil((poolSize * bucketPoolPercent) / 100)
    return size > 0 ? Math.ceil(poolSize / size) : 0
  }

  const validate = (): boolean => {
    const failure = buildValidationRules({
      defaultMinMarginPercent,
      totalWindowMinutes,
      bucketPoolPercent,
      bucketMaxInterpreters,
      bucketIntervalMinutes,
      preAssignmentConfirmationWaitMinutes,
      maxConfirmationRotations,
      preAssignmentConfirmationMinLeadMinutes,
      maxNoShowsInLast3Months,
      minLeadTimeMinutes,
    }).find((rule) => rule.invalid)

    if (failure) {
      toaster('error', failure.message)
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsSaving(true)
    try {
      await axios.put(`${API_URL}/ai-assignment-settings`, {
        isEnabled,
        defaultMinMarginPercent,
        totalWindowMinutes,
        bucketPoolPercent,
        bucketMaxInterpreters,
        bucketIntervalMinutes,
        preAssignmentConfirmationWaitMinutes,
        maxConfirmationRotations,
        preAssignmentConfirmationMinLeadMinutes,
        maxNoShowsInLast3Months,
        minLeadTimeMinutes,
      })
      toaster('success', 'AI Assignment Agent settings saved successfully')
      setIsPersisted(true)
    } catch (error: any) {
      console.error('Error saving AI Assignment Agent settings:', error)
      toaster(
        'error',
        error?.response?.data?.text ??
          error?.response?.data?.message ??
          'Failed to save AI Assignment Agent settings'
      )
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-10'>
        <span className='spinner-border spinner-border-lg'></span>
      </div>
    )
  }

  if (loadFailed) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='text-muted py-6'>
            The AI Assignment Agent settings could not be loaded, so they cannot be edited right
            now. Refresh the page to try again.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card'>
      <div className='card-body'>
        {!isPersisted && (
          <div className='alert alert-light-warning border border-warning py-4 mb-6'>
            <div className='fw-semibold text-gray-800 mb-1'>
              These settings have not been saved yet
            </div>
            <div className='text-muted fs-7'>
              The values below are the defaults. The agent does not run until settings are saved.
            </div>
          </div>
        )}

        <div className='row mb-6'>
          <div className='col-lg-6'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Enable AI Assignment Agent
                <SettingInfoTooltip
                  id='ai-assignment-enabled-info'
                  text='When enabled, the agent contacts qualified interpreters for new in-person appointments and records who was contacted and why.'
                />
              </label>
              <div className='form-check form-switch form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  id='aiAssignmentAgentEnabled'
                  disabled={!canEdit}
                />
                <label
                  className='form-check-label text-gray-600'
                  htmlFor='aiAssignmentAgentEnabled'
                >
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <h4 className='fw-bold text-gray-800 mb-0'>Matching &amp; Margin</h4>
            <div className='separator separator-dashed mt-3 mb-6'></div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-lg-3'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Default Minimum Profit Margin (%)
                <SettingInfoTooltip
                  id='ai-assignment-margin-info'
                  text='Interpreters whose margin is below this are not contacted. Individual clients can override this from their own profile.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={defaultMinMarginPercent}
                onChange={(e) => setDefaultMinMarginPercent(toNumber(e.target.value))}
                min={0}
                max={100}
                step={0.01}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-3'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Total Outreach Window (minutes)
                <SettingInfoTooltip
                  id='ai-assignment-window-info'
                  text='How long the agent waits for replies before choosing. Buckets stop once this elapses.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={totalWindowMinutes}
                onChange={(e) => setTotalWindowMinutes(toNumber(e.target.value))}
                min={1}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-3'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Maximum No-Shows in the Last 3 Months
                <SettingInfoTooltip
                  id='ai-assignment-no-shows-info'
                  text='Interpreters with more no-shows than this in the last three months never enter the pool, so they are never texted and never auto-assigned. This is the same no-show count shown on the Best Matches score. Leave blank to apply no reliability filter; enter 0 to contact only interpreters with a clean three-month record.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={maxNoShowsInLast3Months ?? ''}
                placeholder='Leave blank to apply no reliability filter'
                onChange={(e) => setMaxNoShowsInLast3Months(toOptionalInt(e.target.value))}
                min={0}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-3'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Minimum Notice to Run (minutes)
                <SettingInfoTooltip
                  id='ai-assignment-min-notice-info'
                  text='Appointments booked with less notice than this are left entirely to a coordinator — the agent does not run on them at all. Zero means no minimum, so the agent will take on same-hour bookings. Set this generously: a job starting shortly is exactly when a human should be working the phones.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={minLeadTimeMinutes}
                onChange={(e) => setMinLeadTimeMinutes(toNumber(e.target.value))}
                min={0}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <h4 className='fw-bold text-gray-800 mb-0'>Buckets</h4>
            <div className='separator separator-dashed mt-3 mb-6'></div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-lg-10'>
            <div className='text-muted fs-7 mb-4'>
              Interpreters are sorted by profit margin, highest first, and contacted a bucket at a
              time. Each bucket goes out once the previous one&apos;s time has elapsed without an
              acceptance. Anyone a bucket doesn&apos;t reach is picked up by the next one, ahead of
              lower-margin interpreters &mdash; nobody is skipped over.
            </div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-lg-4'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Bucket Size (% of pool)
                <SettingInfoTooltip
                  id='ai-assignment-bucket-size-info'
                  text='How much of the qualified pool each bucket contacts. At 25% a pool is worked through in four buckets; a smaller share means more, smaller buckets.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={bucketPoolPercent}
                onChange={(e) => setBucketPoolPercent(toNumber(e.target.value))}
                min={0.01}
                max={100}
                step={0.01}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-4'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Maximum Interpreters Per Bucket
                <SettingInfoTooltip
                  id='ai-assignment-bucket-max-info'
                  text='An upper limit on a single bucket, used when it is smaller than the percentage above. Leave blank for no limit.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={bucketMaxInterpreters ?? ''}
                placeholder='No cap'
                onChange={(e) => setBucketMaxInterpreters(toOptionalInt(e.target.value))}
                min={1}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-4'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Time Between Buckets (minutes)
                <SettingInfoTooltip
                  id='ai-assignment-bucket-interval-info'
                  text='How long to wait for replies before sending the next bucket.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={bucketIntervalMinutes}
                onChange={(e) => setBucketIntervalMinutes(toNumber(e.target.value))}
                min={1}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-lg-10'>
            <div className='alert alert-light-primary border border-primary d-flex flex-column py-4'>
              <div className='fw-semibold text-gray-800 mb-1'>
                These settings allow up to {maxBuckets} bucket{maxBuckets === 1 ? '' : 's'} before
                the outreach window closes.
              </div>
              <div className='text-muted fs-7'>
                A pool of 100 interpreters needs {bucketsNeededFor(100)} bucket
                {bucketsNeededFor(100) === 1 ? '' : 's'} to be worked through completely
                {bucketsNeededFor(100) > maxBuckets
                  ? ', so the lowest-margin interpreters would not be reached before the window closes. Shorten the time between buckets, or lengthen the window, to reach everyone.'
                  : '.'}
              </div>
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <h4 className='fw-bold text-gray-800 mb-0'>Assignment Confirmation</h4>
            <div className='text-muted fs-7 mt-2'>
              Before assigning, the agent asks the highest-margin interpreter who accepted to
              confirm they will take the job, and moves on to the next if they decline or do not
              answer.{' '}
              <span className='fw-semibold'>
                This applies only to clients with auto-assignment switched on.
              </span>{' '}
              Everyone else gets a recommendation for a coordinator to act on, and no confirmation
              message is sent.
            </div>
            <div className='separator separator-dashed mt-3 mb-6'></div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-lg-4'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Time to Confirm (minutes per interpreter)
                <SettingInfoTooltip
                  id='ai-assignment-confirm-wait-info'
                  text='How long each interpreter has to confirm before the offer passes to the next one. Set to 0 to switch this off entirely, which assigns the highest-margin accepter outright.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={preAssignmentConfirmationWaitMinutes}
                onChange={(e) => setPreAssignmentConfirmationWaitMinutes(toNumber(e.target.value))}
                min={0}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-4'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Maximum Interpreters to Ask
                <SettingInfoTooltip
                  id='ai-assignment-max-rotations-info'
                  text='Caps how many interpreters are asked before the agent gives up and leaves a recommendation instead. Blank means it keeps going until the cut-off below.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={maxConfirmationRotations ?? ''}
                placeholder='Leave blank for no limit'
                onChange={(e) => setMaxConfirmationRotations(toOptionalInt(e.target.value))}
                min={1}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className='col-lg-4'>
            <div className='mb-5'>
              <label className='form-label fw-bold fs-6 mb-3 d-flex align-items-center gap-2'>
                Stop Asking Before Start (minutes)
                <SettingInfoTooltip
                  id='ai-assignment-min-lead-info'
                  text='The agent stops asking this long before the appointment starts, so a confirmation cannot arrive too late to be useful. At 0 it keeps asking right up to the start time.'
                />
              </label>
              <input
                type='number'
                className='form-control form-control-solid'
                value={preAssignmentConfirmationMinLeadMinutes}
                onChange={(e) =>
                  setPreAssignmentConfirmationMinLeadMinutes(toNumber(e.target.value))
                }
                min={0}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        {canEdit && (
          <div className='row'>
            <div className='col-lg-6'>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2'></span>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
